import { getAssetPath } from '@/constants/configs';
import {
  defaultConfigs,
  getExtraBasicInfoQuestions as defaultGetExtraBasicInfoQuestions,
  resolveICBWebUrl,
} from '@/constants/tenant-configs/default';

import { AccountType, AccountTypeStatus } from '@/types/api/account';
import { QuestionCode } from '@/types/api/pep';
import { AppConfigurations } from '@/types/app-configurations';
import { DocumentType } from '@/types/document';
import { Profession, User } from '@/types/user';

import {
  isBusinessPurposeQuestionEnabled,
  isExistingCustomer,
  isMinorUser,
  isRightStartUser,
} from '@/utils/user';
import { isAboveYearsOld } from '@/utils/validation';

export const saintLuciaConfigs: AppConfigurations = {
  ...defaultConfigs,
  links: {
    ...defaultConfigs.links,
    privacyPolicyURL:
      'https://www.republicbankstlucia.com/about/privacy-policy',
    termsAndConditionsURL:
      'https://republiconline.republictt.com/lc/legal.html',
    termsOfUseURL: getAssetPath('/tnc/republiconboard-tnc-lca.pdf'),
    appTermsAndConditionsURL: getAssetPath('/tnc/republiconboard-tnc-lca.pdf'),
    biometricConsentURL: getAssetPath(
      '/bio-consent/republiconboard-biometric-consent-lca.pdf',
    ),
    eFreeURL: 'https://www.republicbankstlucia.com/personal/e-free-account',
    savingsPlusURL:
      'https://www.republicbankstlucia.com/personal/savings-plus-account',
    icbWeb: resolveICBWebUrl(
      'https://republiconlineec.rfhl.com/RBEC.UI/#/administrationGeneral/login',
    ),
    icbIOS: 'https://apps.apple.com/us/app/republicmobile-ec/id1582093121',
    icbAndroid:
      'https://play.google.com/store/apps/details?id=ecicprod.com.republicapp',
  },
  defaultCountryCode: '1758',
  locale: 'en-LC',
  isoCode: 'LCA',
  dateFormat: 'dd/MM/yyyy',
  defaultCurrency: 'XCD',
  idCaptureRequirement: 'idCardOrPassport',
  idDocumentTypes: [DocumentType.Id, DocumentType.Passport],
  minorOnlyIdDocumentTypes: [DocumentType.BirthCertificate],
  showRiskSectorFlagQuestion: false,
  showSelfEmployedWithRiskSectorFlagQuestion: true,
  existingCustomer: {
    maxAccountsPerType: 2,
  },
  autoApproveUnemployedProfessions: [
    Profession.Pensioner,
    Profession.Housewife,
    Profession.Unemployed,
  ],
  jointAccountRelationshipOptions: [
    { value: 'PARENT', i18nKey: 'minorOthers.form.relationship.parent' },
    {
      value: 'LEGAL_GUARDIAN',
      i18nKey: 'minorOthers.form.relationship.legalGuardian',
    },
  ],
};

export const getExtraBasicInfoQuestions = (user: User) => {
  if (isMinorUser(user)) {
    return {
      ...defaultGetExtraBasicInfoQuestions(user),
      [QuestionCode.MinorConsentSign]: {
        enabled:
          isAboveYearsOld(13, user?.birthday) &&
          user?.hasPhoneNumber === true &&
          user?.allowToDebitCardOnlineBanking === undefined,
        question: 'questionDrawer.minorConsentSign',
        priority: 3,
      },
    };
  }

  return {
    ...defaultGetExtraBasicInfoQuestions(user),
    [QuestionCode.AccountPurpose]: {
      enabled: isBusinessPurposeQuestionEnabled(user),
      question: 'questionDrawer.accountPurpose.question',
      priority: 0,
    },
    [QuestionCode.EXCPrimaryAccount]: {
      enabled:
        isExistingCustomer(user) &&
        !isRightStartUser(user) &&
        !user.chargeAccountNo &&
        !!user.accountTypes?.find(
          type =>
            type.status === AccountTypeStatus.Active &&
            type.type === AccountType.Checking,
        ),
      question: 'questionDrawer.EXCPrimaryAccount.question',
      description: 'questionDrawer.EXCPrimaryAccount.description',
      priority: 5,
    },
  };
};
